"use client";

import React from "react";

function useNavigation(navItems, parentSelector = "body", scrollOffset = 100) {
  const [finalNavItems, setFinalNavItems] = React.useState([]);
  const [currentVisibleItem, setCurrentVisibleItem] = React.useState(null);
  const observerRef = React.useRef(null);
  const intersectionObserverRef = React.useRef(null);
  const targetElementsRef = React.useRef(new Map());

  // First useEffect: Validate target IDs and observe DOM changes
  React.useEffect(() => {
    const parentElement = document.querySelector(parentSelector);
    if (!parentElement) return;

    const validateNavItems = () => {
      const validatedItems = navItems.map((item) => {
        const targetElement = parentElement.querySelector(item.target);
        return {
          ...item,
          isActive: !!targetElement,
        };
      });
      setFinalNavItems(validatedItems);
    };

    // Initial validation
    validateNavItems();

    // Set up MutationObserver to watch for DOM changes
    observerRef.current = new MutationObserver((mutations) => {
      let shouldRevalidate = false;

      mutations.forEach((mutation) => {
        // Check if any nodes were added or removed
        if (mutation.type === "childList") {
          const addedNodes = Array.from(mutation.addedNodes);
          const removedNodes = Array.from(mutation.removedNodes);

          // Check if any added/removed nodes contain our target IDs
          [...addedNodes, ...removedNodes].forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              navItems.forEach((item) => {
                const targetId = item.target.substring(1); // Remove '#' from target
                if (node.id === targetId || node.querySelector?.(item.target)) {
                  shouldRevalidate = true;
                }
              });
            }
          });
        }

        // Check if any attributes changed (like id attributes)
        if (mutation.type === "attributes" && mutation.attributeName === "id") {
          shouldRevalidate = true;
        }
      });

      if (shouldRevalidate) {
        validateNavItems();
      }
    });

    // Start observing
    observerRef.current.observe(parentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["id"],
    });

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [navItems, parentSelector]);

  // Second useEffect: Track visible nav items on scroll
  React.useEffect(() => {
    const parentElement = document.querySelector(parentSelector);
    if (!parentElement) return;

    const updateVisibleTargets = () => {
      targetElementsRef.current.clear();

      finalNavItems.forEach((item) => {
        if (item.isActive) {
          const element = parentElement.querySelector(item.target);
          if (element) {
            targetElementsRef.current.set(item.target, element);
          }
        }
      });
    };

    const checkCurrentVisibleItem = () => {
      if (targetElementsRef.current.size === 0) return;

      let currentItem = null;
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;

      // Convert Map to Array and sort by document order
      const sortedElements = Array.from(
        targetElementsRef.current.entries()
      ).sort((a, b) => {
        const positionA = a[1].getBoundingClientRect().top + scrollTop;
        const positionB = b[1].getBoundingClientRect().top + scrollTop;
        return positionA - positionB;
      });

      for (let i = 0; i < sortedElements.length; i++) {
        const [targetId, element] = sortedElements[i];
        const rect = element.getBoundingClientRect();

        // Element's top position relative to viewport
        const elementTop = rect.top;
        const elementBottom = rect.bottom;

        // Check if element's top is above or at the offset line (100px from top)
        if (elementTop <= scrollOffset) {
          // This element has passed the offset line
          // Check if its bottom is still below the offset line
          if (elementBottom > scrollOffset) {
            // Element is currently spanning across the offset line
            const navItem = finalNavItems.find(
              (item) => item.target === targetId
            );
            currentItem = navItem;
          }
          // If bottom is above offset line, continue to check next element
        } else {
          // Element's top hasn't reached the offset line yet
          // If we haven't found any item yet, and this is the first element
          // that appears on screen, use the previous one (if exists)
          if (!currentItem && i > 0) {
            const [prevTargetId] = sortedElements[i - 1];
            const prevNavItem = finalNavItems.find(
              (item) => item.target === prevTargetId
            );
            currentItem = prevNavItem;
          }
          break;
        }
      }

      // If no item found but we have elements, use the first one if it's visible
      if (!currentItem && sortedElements.length > 0) {
        const [firstTargetId, firstElement] = sortedElements[0];
        const firstRect = firstElement.getBoundingClientRect();
        if (firstRect.bottom > 0 && firstRect.top < window.innerHeight) {
          const firstNavItem = finalNavItems.find(
            (item) => item.target === firstTargetId
          );
          currentItem = firstNavItem;
        }
      }

      setCurrentVisibleItem(currentItem);
    };

    const handleScroll = () => {
      requestAnimationFrame(checkCurrentVisibleItem);
    };

    const setupScrollListener = () => {
      updateVisibleTargets();

      if (targetElementsRef.current.size > 0) {
        // Initial check
        checkCurrentVisibleItem();

        // Add scroll listener
        window.addEventListener("scroll", handleScroll, { passive: true });
      }
    };

    if (finalNavItems.length > 0) {
      setTimeout(setupScrollListener, 100);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
      }
    };
  }, [finalNavItems, parentSelector, scrollOffset]);

  return {
    finalNavItems,
    currentVisibleItem,
  };
}

export default useNavigation;
